import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { useToasts } from '../../lib/toast';
import type { StationPrinter } from '@taj/types';

interface Row extends StationPrinter {
  ip: string;
  port: string;
  active: boolean;
}

export function PrintersManager({ onBack }: { onBack: () => void }) {
  const push = useToasts((s) => s.push);
  const [rows, setRows] = useState<Row[]>([]);

  const apply = (list: StationPrinter[]) =>
    setRows(list.map((s) => ({ ...s, ip: s.printer?.ip ?? '', port: String(s.printer?.port ?? 9100), active: s.printer?.active ?? true })));
  const load = () => api.listPrinters().then(apply).catch((e) => push(e.message, 'warn'));
  useEffect(() => { load(); }, []);

  const add = async () => {
    const name = prompt('Nom du nouveau poste (ex. « Borne 3 », « Bar », « Caisse 2 ») :');
    if (!name || !name.trim()) return;
    try { apply(await api.addStation(name.trim())); push(`✅ Poste « ${name.trim()} » ajouté`); }
    catch (e: any) { push(e.message, 'warn'); }
  };
  const remove = async (row: Row) => {
    if (!confirm(`Supprimer le poste « ${row.stationName} » et son imprimante ?`)) return;
    try { apply(await api.deleteStation(row.stationId)); push(`🗑️ « ${row.stationName} » supprimé`); }
    catch (e: any) { push(e.message, 'warn'); }
  };

  const set = (i: number, patch: Partial<Row>) => setRows((r) => r.map((x, k) => (k === i ? { ...x, ...patch } : x)));

  const save = async (row: Row) => {
    if (!row.ip.trim()) return push('Indique l’adresse IP de l’imprimante', 'warn');
    try {
      await api.setPrinter(row.stationId, { ip: row.ip.trim(), port: parseInt(row.port, 10) || 9100, active: row.active });
      push(`✅ Imprimante ${row.stationName} enregistrée`);
      load();
    } catch (e: any) { push(e.message, 'warn'); }
  };
  const test = async (row: Row) => {
    push(`🖨️ Test d’impression ${row.stationName}…`);
    try { await api.testPrinter(row.stationId); push(`✅ Ticket de test envoyé à ${row.stationName}`); }
    catch (e: any) { push(`❌ ${row.stationName} : ${e.message}`, 'warn'); }
  };

  // Rediriger toutes les impressions vers une seule imprimante (ex. si une seule marche).
  const redirectAll = async () => {
    const current = rows.find((r) => r.ip)?.ip ?? '';
    const ip = prompt('Rediriger TOUS les postes vers cette adresse IP (une imprimante qui fonctionne) :', current);
    if (!ip || !ip.trim()) return;
    if (!confirm(`Envoyer les ${rows.length} postes vers ${ip.trim()} ?\nVous pourrez les remettre individuellement ensuite.`)) return;
    try {
      for (const row of rows) {
        await api.setPrinter(row.stationId, { ip: ip.trim(), port: parseInt(row.port, 10) || 9100, active: true });
      }
      push(`✅ Tous les postes redirigés vers ${ip.trim()}`);
      load();
    } catch (e: any) { push(e.message, 'warn'); }
  };

  return (
    <div className="staff-scroll">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '4px 0 14px' }}>
        <button className="km-back" onClick={onBack}>←</button>
        <div className="staff-title" style={{ fontSize: '1.4rem', flex: 1 }}>🖨️ Imprimantes par poste</div>
      </div>
      <div className="staff-subtitle" style={{ margin: '0 2px 14px' }}>
        Une imprimante (IP / ESC-POS) par poste. Le ticket s’imprime automatiquement quand une commande arrive.
        <br />🛟 <b>Secours</b> = imprimante de repli automatique si un poste est injoignable · 🏷️ <b>Étiquettes</b> = étiqueteuse des plats.
      </div>

      {rows.map((row, i) => (
        <div key={row.stationId} className="cc" style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: '1.3rem', flex: 1 }}>👨‍🍳 {row.stationName}</div>
            <div className="al-toggle" style={{ padding: '6px 10px', margin: 0, gap: 8 }}>
              <span className="an" style={{ fontSize: '.78rem' }}>{row.active ? 'Active' : 'Inactive'}</span>
              <div className={`al-sw${row.active ? ' on' : ''}`} onClick={() => set(i, { active: !row.active })} />
            </div>
            {row.removable && (
              <button
                className="cf-reprint"
                style={{ margin: '0 0 0 8px', padding: '6px 10px', color: '#b23', borderColor: '#e0b4b4' }}
                title="Supprimer ce poste"
                onClick={() => remove(row)}
              >🗑️</button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 2 }}>
              <div style={{ fontSize: '.68rem', fontWeight: 800, color: 'var(--ink-dim)', textTransform: 'uppercase', marginBottom: 5 }}>Adresse IP</div>
              <input className="inp" placeholder="192.168.1.50" value={row.ip} onChange={(e) => set(i, { ip: e.target.value })} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '.68rem', fontWeight: 800, color: 'var(--ink-dim)', textTransform: 'uppercase', marginBottom: 5 }}>Port</div>
              <input className="inp" inputMode="numeric" value={row.port} onChange={(e) => set(i, { port: e.target.value })} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="btn-gold" style={{ flex: 1, fontSize: '1rem', padding: '11px' }} onClick={() => save(row)}>💾 Enregistrer</button>
            <button className="cf-reprint" style={{ flex: 1, margin: 0 }} onClick={() => test(row)} disabled={!row.printer && !row.ip}>🖨️ Test</button>
          </div>
        </div>
      ))}
      {rows.length === 0 && <div className="no-data">Aucun poste. Ajoute-en un ci-dessous.</div>}

      <button className="cf-reprint" style={{ width: '100%', marginTop: 10 }} onClick={redirectAll}>
        🔀 Tout rediriger vers une seule IP
      </button>
      <button className="btn-gold" style={{ width: '100%', marginTop: 8 }} onClick={add}>
        ➕ Ajouter une imprimante / borne
      </button>
    </div>
  );
}
